import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react'
import { privateApi } from '../../../api/api';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import CurrencyInput from '../../../shared/components/form/CurrencyInput';
import { formatPrice } from '../../../shared/utils/formatPrice';
import DateInput from '../../../shared/components/form/DateInput';

interface RateFormRestrictionsProps {
    field: any;
    control: any;
    watch: any;
    setValue?: any;
}

const RateFormRestrictions: React.FC<RateFormRestrictionsProps> = ({
    field,
    control,
    watch,
    setValue
}) => {
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const { data: roomTypes, isFetching, isSuccess, isError } = useQuery({
        queryKey: ['rooms', 'rates', 'restrictions'],
        queryFn: () => privateApi.get(`/v4/room-type`, {
            params: {
                all: 1,
            }
        }).then((response) => {
            return response.data.data.map((roomType: any) => ({
                label: roomType.name,
                value: roomType.id,
            }));
        }),
    });

    if (isError) {
        return <div>Something went wrong</div>;
    }

    const { data: rateplans, isFetching: isFetchingRateplans, isSuccess: isSuccessRateplans, isError: isErrorRateplans } = useQuery({
        queryKey: ['rates', 'restrictions', watch(`${field}.values.room_type_id`)],
        queryFn: () => privateApi.get(`/v4/rate-plan`, {
            params: {
                filter: {
                    room_type_id: watch(`${field}.values.room_type_id`).value,
                },
                all: 1,
            }
        }).then((response) => {
            setValue(`${field}.values.rate_plan_id`, null);
            return response.data.data
                .filter((rateplan: any) => rateplan.type === 'rate') // Filter hanya yang tipe 'rate'
                .map((rateplan: any) => ({
                    label: rateplan.name,
                    value: rateplan.id,
                    min_rate: rateplan.min_rate,
                }));
        }),
        enabled: !!watch(`${field}.values.room_type_id`),
    });

    if (isErrorRateplans) {
        return <div>Something went wrong</div>;
    }

    return (
        <>
            {isFetching && <FormSpinner />}
            {isSuccess && (
                <div>
                    <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label d-flex justify-content-end">
                            Room Title:
                        </label>
                        <div className="col-sm-8">
                            <Controller
                                name={`${field}.values.room_type_id`}
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={roomTypes}
                                        value={roomTypes.find((option: { value: any; label: string }) => option.value === field.value)}
                                        placeholder="Select room title"
                                        onChange={(selectedOption) => {
                                            field.onChange(selectedOption);
                                        }}
                                        isLoading={isFetching}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    {isFetchingRateplans && <FormSpinner />}
                    {isSuccessRateplans && !isFetchingRateplans && (
                        <>
                            <div className="row mb-3 align-items-center">
                                <label className="col-sm-4 col-form-label d-flex justify-content-end">
                                    Rateplan:
                                </label>
                                <div className="col-sm-8">
                                    <Controller
                                        name={`${field}.values.rate_plan_id`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={rateplans}
                                                value={rateplans.find((option: { value: any; label: string }) => option.value === field.value)}
                                                placeholder="Select rateplan"
                                                onChange={(selectedOption) => {
                                                    field.onChange(selectedOption);
                                                }}
                                                isLoading={isFetchingRateplans}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {watch(`${field}.values.rate_plan_id`)?.value && (
                                <>
                                    <div className="row mb-3 align-items-center">
                                        <label className="col-sm-4 col-form-label d-flex justify-content-end">
                                            From Date:
                                        </label>
                                        <div className="col-sm-8">
                                            <Controller
                                                name={`${field}.values.date_from`}
                                                control={control}
                                                render={({ field }) => (
                                                    <DateInput
                                                        {...field}
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value);
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3 align-items-center">
                                        <label className="col-sm-4 col-form-label d-flex justify-content-end">
                                            To Date:
                                        </label>
                                        <div className="col-sm-8">
                                            <Controller
                                                name={`${field}.values.date_to`}
                                                control={control}
                                                render={({ field }) => (
                                                    <DateInput
                                                        {...field}
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value);
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3 align-items-center">
                                        <label className="col-sm-4 col-form-label d-flex justify-content-end">
                                            Value:
                                        </label>
                                        <div className="col-sm-8">
                                            <Controller
                                                name={`${field}.values.value`}
                                                control={control}
                                                render={({ field: localField }) => (
                                                    <CurrencyInput
                                                        {...localField}
                                                        value={localField.value}
                                                        onChange={(_e, rawValue) => {
                                                            const value = Number(rawValue);
                                                            const minimumRate = rateplans.find((rateplan: { value: any; }) => rateplan.value === watch(`${field}.values.rate_plan_id`)?.value)?.min_rate;

                                                            console.log('minimumRate', minimumRate);

                                                            if (minimumRate) {
                                                                if (value < minimumRate) {
                                                                    setErrorMessage(`Value must be greater than or equal to ${formatPrice(parseInt(minimumRate), "IDR", "id-ID")}`);
                                                                } else {
                                                                    setErrorMessage(undefined);
                                                                }
                                                            } else {
                                                                setErrorMessage(undefined);
                                                            }

                                                            localField.onChange(rawValue);
                                                        }}
                                                        error={errorMessage}
                                                        placeholder="Enter price"
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}
        </>
    )
}

export default RateFormRestrictions